import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { depositKeys, estimateKeys, invoiceKeys, jobKeys } from '@/lib/queryKeys';
import { billingV2Endpoints } from '@/lib/api/endpoints';
import { billingV2Post } from '@/lib/api/clients/billing-v2';
import type { CaptureDepositInput, Deposit } from '@/types/deposit';

/** Spec §4.1 — staff-side capture flow (cash/check/card/ACH). */
export function useCaptureDeposit() {
  const queryClient = useQueryClient();

  return useApiMutation<Deposit, CaptureDepositInput>({
    mutationFn: (input) =>
      billingV2Post<Deposit, CaptureDepositInput>(billingV2Endpoints.depositCapture(), input),
    onSuccess: (deposit, input) => {
      queryClient.setQueryData<Deposit>(depositKeys.detail(deposit.id), deposit);
      void queryClient.invalidateQueries({ queryKey: depositKeys.all() });
      // A deposit on an estimate marks the estimate collected, and the estimate
      // screens decide from that whether to offer Collect deposit again.
      if (input.estimateId) {
        void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(input.estimateId) });
        void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      }
      // A job's deposit marks the job's deposit received, which its Payments
      // section reads, and a held one is applied to the job's open invoices.
      if (input.jobId) {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(input.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.payments(input.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(input.jobId) });
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(input.jobId) });
        // The rest went to the job as a payment, which the job lists read.
        if (input.remainderToJob) void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      }
      const amount = `$${Number(deposit.amount ?? 0).toFixed(2)}`;
      if (input.remainderToJob && deposit.remainderPaymentId) {
        const rest = Math.max(
          0,
          Math.round(input.amount * 100) - Math.round(Number(deposit.amount ?? 0) * 100),
        );
        toast.success(`Deposit captured: ${amount}`, {
          description: `The other $${(rest / 100).toFixed(2)} was recorded as a payment on the job.`,
        });
        return;
      }
      if (deposit.status === 'CAPTURE_PENDING') {
        // Submitted, not collected. The server charges on the saved method's
        // own rail, which this form cannot see, so the copy holds for both: a
        // bank debit clearing, or a card whose processor answer is unconfirmed.
        toast.success(`Deposit submitted: ${amount}`, {
          description:
            'The deposit can be used once the payment settles. A bank payment usually takes a few business days.',
        });
        return;
      }
      toast.success(`Deposit captured: ${amount}`);
    },
  });
}
